/**
 * Sorting a refused sign-in into the box it belongs in.
 *
 * Three boxes on the login page: red for a credential failure, amber for
 * "you are fine, the account isn't ready" (pending, rejected, disabled), and
 * amber-with-a-button for "you never clicked the link" (unverified), which is
 * the one the person can fix themselves.
 *
 * The decision keys on the 403's `code`, which the API sends alongside the
 * sentence (see `_inactive_reason` in jcseo/api/auth.py). It used to be a
 * regex on the sentence — /approv/i — which put "not approved" and "awaiting
 * approval" in amber and "disabled" in red by accident of wording.
 *
 * Dependency-free so it can be tested without the API module.
 */
import type { LoginNoticeCode } from '../types';

export type LoginRefusal = {
  kind: 'error' | 'notice' | 'unverified';
  message: string;
};

/** What a redirect back to `/login?notice=<code>` should say. The Google
 *  callback lands here, having no body of its own to carry a sentence. */
export const NOTICE_TEXT: Record<LoginNoticeCode, string> = {
  disabled: 'Your account has been disabled. Contact an administrator.',
  rejected: 'Your account application was not approved.',
  unverified: 'Check your inbox for a verification link before signing in.',
  pending:
    "Your account is awaiting admin approval. You'll be able to sign in once it's approved.",
  google_failed: 'Google sign-in did not complete. Please try again.',
};

function isNoticeCode(code: string): code is LoginNoticeCode {
  // Own property only: `'constructor' in {}` is true through the prototype,
  // and this code comes from a query string anyone can write.
  return Object.prototype.hasOwnProperty.call(NOTICE_TEXT, code);
}

/** The refusal a `?notice=` query parameter stands for, or null for none or
 *  an unknown code (anyone can type a query string). */
export function refusalForNotice(code: string | null): LoginRefusal | null {
  if (!code || !isNoticeCode(code)) return null;
  return {
    kind: code === 'unverified' ? 'unverified' : code === 'google_failed' ? 'error' : 'notice',
    message: NOTICE_TEXT[code],
  };
}

export function classifyLoginError(err: unknown): LoginRefusal {
  const message = err instanceof Error && err.message ? err.message : 'Failed to login';
  const status = (err as { status?: unknown })?.status;
  const code = (err as { code?: unknown })?.code;
  if (status === 403 && typeof code === 'string') {
    if (code === 'unverified') return { kind: 'unverified', message };
    return { kind: 'notice', message };
  }
  return { kind: 'error', message };
}
